import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import AddObjectModal from '../components/AddObjectModal'
// Unity prefabs catalog will be fetched from `/unity-prefabs.json` in public at runtime

const Container = styled.div`
  background-color: #f5f5f5;
  height: 100vh;
  padding: 0;
  overflow: hidden;

  @media (min-width: 681px) {
    display: flex;
    flex-direction: row;
  }
`

const Header = styled.div`
  background-color: white;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  
  @media (min-width: 681px) {
    grid-column: 1 / -1;
  }
`

// 데스크톱용 좌우 섹션
const LeftSection = styled.div`
  display: none;

  @media (min-width: 681px) {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: white;
    border-right: 1px solid #e0e0e0;
    height: 100vh;
    overflow-y: auto;
  }
`

const RightSection = styled.div`
  display: none;

  @media (min-width: 681px) {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: white;
    height: 100vh;
    overflow: hidden;
  }
`

// Floating add button for creating new objects
const FixedAddButton = styled.button`
  position: fixed;
  right: 20px;
  bottom: 20px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 12px 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 8px 20px rgba(37,99,235,0.35);
  cursor: pointer;
  z-index: 500;
`

// 모바일용 기존 컴포넌트들
const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow-y: auto;

  @media (min-width: 681px) {
    display: none;
  }
`

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  .logo {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 14px;
  }
`

const ARStageContainer = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  min-height: 280px;
`

const ARStage = styled.div`
  position: relative;
  width: 320px;
  height: 220px;
  background-image: url('/Arstage.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    width: 280px;
    height: 190px;
  }
  
  @media (max-width: 480px) {
    width: 240px;
    height: 160px;
  }
`

const ARObject = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -120%);
  transition: all 0.5s ease;

  img {
    width: 150px;
    height: auto;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));

    @media (max-width: 768px) {
      width: 120px;
    }

    @media (max-width: 480px) {
      width: 100px;
    }
  }
`

const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 350px;
  margin-top: 10px;
  gap: 20px;
  
  @media (max-width: 768px) {
    max-width: 300px;
    margin-top: 5px;
  }
  
  @media (max-width: 480px) {
    max-width: 260px;
    margin-top: 5px;
  }
`

const NavButton = styled.button`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #2196F3;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #2196F3;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    color: #ccc;
    cursor: not-allowed;
    box-shadow: none;
    
    &:hover {
      transform: none;
    }
  }
`

// 퀴즈 제목 표시 컴포넌트
const QuizTitleHeader = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin: 20px 20px 15px 20px;
  
  @media (max-width: 680px) {
    margin: 15px 20px 10px 20px;
  }
`

const QuizTitleText = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
`

const QuizTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (max-width: 680px) {
    white-space: normal;
    word-break: break-word;
  }
`

const QuizId = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  font-family: 'Courier New', monospace;
`

const TabsContainer = styled.div`
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: center;
  padding: 0 20px;
`

const TabsWrapper = styled.div`
  display: flex;
  max-width: 400px;
  width: 100%;
`

const Tab = styled.button`
  flex: 1;
  padding: 16px 8px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.$active ? '#2196F3' : '#666'};
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background-color: ${props => props.$active ? '#2196F3' : 'transparent'};
  }
  
  &:hover {
    color: #2196F3;
  }
`

const CategorySelector = styled.div`
  padding: 15px 20px;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  position: relative;
`

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`

const DropdownButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  
  &:hover {
    border-color: #2196F3;
  }
`

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
`

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &.selected {
    background-color: #e3f2fd;
    color: #2196F3;
  }
`

const ObjectGrid = styled.div`
  padding: 20px;
  background-color: white;
  flex: 1;
  overflow-y: auto;
`

const ObjectGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  max-width: 450px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    max-width: 350px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    max-width: 300px;
  }
`

// 데스크톱용 스타일 컴포넌트들
const DesktopSectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 20px 0;
  padding: 20px 20px 0 20px;
`

const DesktopTabsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 20px 20px 20px;
  border-bottom: 1px solid #e0e0e0;
`

const DesktopTab = styled.button`
  padding: 8px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  background-color: ${props => props.$active ? '#2196F3' : 'white'};
  color: ${props => props.$active ? 'white' : '#666'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$active ? '#1976D2' : '#f0f0f0'};
  }
`

const DesktopCategorySection = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
`

const DesktopCategoryLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
  display: block;
`

const DesktopObjectGrid = styled.div`
  padding: 20px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

const DesktopObjectGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
`

const DesktopARStageContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  gap: 0;
  height: 100vh;
  overflow: hidden;
`

const DesktopStageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0;
`

const DesktopButtonSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  margin: 0;
  flex-shrink: 0;
`

const DesktopARStage = styled.div`
  position: relative;
  width: 280px;
  height: 200px;
  background-image: url('/ArStage.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
`


const ObjectCard = styled.button`
  aspect-ratio: 1;
  border: 2px solid ${props => props.$selected ? '#2196F3' : '#e0e0e0'};
  border-radius: 12px;
  background-color: ${props => props.$selected ? '#f3f8ff' : 'white'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: #2196F3;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15);
  }
  
  img {
    width: 70%;
    height: 70%;
    object-fit: contain;
  }
`

/* === 새 디자인: 4단계 stepper 헤더 (옵션 A 외형 shell) === */
const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 28px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 50;
  flex-wrap: wrap;

  @media (max-width: 1100px) {
    padding: 12px 16px;
  }
`

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  border: 1px solid #e5e7eb;
  color: #475569;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  &:hover { border-color: #7c3aed; color: #7c3aed; }
`

const StepperWrap = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 720px;
  justify-content: center;
  margin: 0 12px;

  @media (max-width: 1280px) {
    order: 3;
    width: 100%;
    margin: 8px 0 0;
    max-width: none;
  }
`

const StepDot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  .num {
    width: 28px; height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    background: ${p => p.$active ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : '#e5e7eb'};
    color: ${p => p.$active ? '#ffffff' : '#94a3b8'};
    box-shadow: ${p => p.$active ? '0 4px 12px rgba(124,58,237,0.30)' : 'none'};
  }
  .label {
    font-size: 13px;
    font-weight: 600;
    color: ${p => p.$active ? '#0f172a' : '#94a3b8'};
  }
`

const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background: #e5e7eb;
  margin: 0 10px;
  min-width: 16px;
`

const TopActions = styled.div`
  display: flex;
  gap: 8px;
`

const SaveDraftBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  color: #0f172a;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #7c3aed; color: #7c3aed; }
`

const NextStepBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(124,58,237,0.30);
  transition: all 0.15s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 10px 22px rgba(124,58,237,0.40); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

/* === 2단계 본문 새 디자인 === */
const Stage = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 20px;
  padding: 24px 28px;
  background: #f8fafc;
  min-height: calc(100vh - 64px);
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    padding: 16px;
  }
`

const PanelCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`

const PanelCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const PanelLabel = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: #475569;
  letter-spacing: -0.2px;
`

const TitleStrong = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  word-break: keep-all;
`

const TitleIdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #94a3b8;

  .badge {
    background: #f1f5f9;
    color: #475569;
    padding: 3px 10px;
    border-radius: 999px;
    font-weight: 600;
  }
`

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
`

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const ChipBtn = styled.button`
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid ${p => p.$active ? 'transparent' : '#e5e7eb'};
  background: ${p => p.$active ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : '#ffffff'};
  color: ${p => p.$active ? '#ffffff' : '#475569'};
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  box-shadow: ${p => p.$active ? '0 4px 12px rgba(124,58,237,0.25)' : 'none'};

  &:hover:not(:disabled) {
    border-color: #7c3aed;
    color: ${p => p.$active ? '#ffffff' : '#7c3aed'};
  }
`

const SubDropdown = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  font-size: 13.5px;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #7c3aed; }
`

const SubMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 280px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.10);
  z-index: 20;
`

const SubMenuItem = styled.div`
  padding: 11px 14px;
  font-size: 13px;
  color: #0f172a;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.12s;
  &:hover { background: #f5f3ff; color: #7c3aed; }
  &.selected { background: #ede9fe; color: #7c3aed; font-weight: 700; }
  &:last-child { border-bottom: none; }
  .sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }
`

const DropdownContainerNew = styled.div`
  position: relative;
`

const ObjectsGridNew = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
`

const ObjectCardNew = styled.button`
  position: relative;
  aspect-ratio: 1;
  border-radius: 12px;
  border: 2px solid ${p => p.$selected ? '#7c3aed' : '#e5e7eb'};
  background: ${p => p.$selected ? '#f5f3ff' : '#ffffff'};
  cursor: pointer;
  overflow: hidden;
  padding: 0;
  transition: all 0.15s;
  &:hover { border-color: #7c3aed; transform: translateY(-2px); }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8px;
  }
`

const EmptyNote = styled.div`
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
  padding: 24px 12px;
`

/* === 우측 AR 미리보기 + 설정 === */
const PreviewWrap = styled.div`
  position: relative;
  background: linear-gradient(135deg, #ede9fe 0%, #dbeafe 100%);
  border-radius: 14px;
  aspect-ratio: 16 / 11;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PreviewObjectImg = styled.img`
  max-width: 60%;
  max-height: 70%;
  object-fit: contain;
  filter: drop-shadow(0 20px 30px rgba(0, 0, 0, 0.18));
`

const PreviewPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
  padding: 20px;
`

const HelpFloat = styled.button`
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  background: rgba(255, 255, 255, 0.95);
  color: #475569;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: #7c3aed; color: #7c3aed; }
`

const ARSettingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`

const ARSettingCardItem = styled.button`
  text-align: left;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #7c3aed; transform: translateY(-1px); }

  .icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${p => p.$bg};
    color: ${p => p.$color};
  }
  .info {
    flex: 1;
    min-width: 0;
    strong {
      display: block;
      font-size: 13px;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 2px;
    }
    span {
      font-size: 11px;
      color: #64748b;
      line-height: 1.4;
    }
  }
`

const SelectedBanner = styled.div`
  background: #ede9fe;
  border: 1px solid #c4b5fd;
  color: #5b21b6;
  padding: 9px 14px;
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 600;
`

/* === 빠진 디자인 보강 === */
const TitleInputBox = styled.div`
  position: relative;
`

const TitleInput = styled.input`
  width: 100%;
  padding: 11px 60px 11px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  background: #ffffff;
  color: #0f172a;
  transition: all 0.15s;

  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124,58,237,0.10);
  }
`

const TitleCharCount = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  color: #94a3b8;
  pointer-events: none;
`

const ChipsRowCollapse = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  max-height: ${p => p.$expanded ? 'none' : '74px'};
  overflow: hidden;
  transition: max-height 0.2s;
`

const MoreChipsBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #475569;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: #7c3aed; color: #7c3aed; }
`

const CoverDrop2 = styled.div`
  border: 2px dashed ${p => p.$drag ? '#7c3aed' : '#e5e7eb'};
  border-radius: 12px;
  padding: 18px;
  text-align: center;
  cursor: pointer;
  background: ${p => p.$drag ? 'rgba(124,58,237,0.06)' : '#fafafa'};
  transition: all 0.15s;
  &:hover { border-color: #7c3aed; }

  img.cover2-preview {
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    border-radius: 8px;
    display: block;
  }
`

const CoverDrop2Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 13px;
  color: #475569;

  .icon {
    width: 36px; height: 36px;
    border-radius: 10px;
    background: rgba(124,58,237,0.10);
    color: #7c3aed;
    display: flex; align-items: center; justify-content: center;
  }
  .text strong { display: block; color: #0f172a; font-weight: 600; font-size: 13px; }
  .text span { font-size: 11px; color: #94a3b8; }
`

const RemoveCoverBtn2 = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px; height: 26px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,0.55);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  &:hover { background: rgba(0,0,0,0.85); }
`

const PreviewImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;

  @media (max-width: 700px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const PreviewSlot = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: ${p => p.$image ? `url(${p.$image}) center/cover` : '#fafafa'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #94a3b8;
  text-align: center;

  .remove {
    position: absolute;
    top: 4px; right: 4px;
    width: 20px; height: 20px;
    border-radius: 50%;
    border: none;
    background: rgba(0,0,0,0.6);
    color: #fff;
    cursor: pointer;
    font-size: 11px;
    display: flex; align-items: center; justify-content: center;
  }
`

const AddPreviewBtn = styled.button`
  position: relative;
  aspect-ratio: 1;
  border-radius: 10px;
  border: 1.5px dashed #c4b5fd;
  background: rgba(124,58,237,0.04);
  color: #7c3aed;
  font-size: 22px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;

  &:hover { background: rgba(124,58,237,0.10); border-color: #7c3aed; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }

  span.label {
    font-size: 10px;
    color: #7c3aed;
    font-weight: 600;
  }
`

const PreviewCountText = styled.div`
  text-align: right;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
`

const ViewToggleBox = styled.div`
  display: inline-flex;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 3px;
  background: #f8fafc;
  gap: 2px;
`

const ViewToggleBtn = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  background: ${p => p.$active ? '#ffffff' : 'transparent'};
  color: ${p => p.$active ? '#7c3aed' : '#64748b'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${p => p.$active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'};
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const PreviewToolbar = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(255,255,255,0.95);
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 6px;
`

const ToolFloatBtn = styled.button`
  width: 38px;
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 6px 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  color: ${p => p.$active ? '#7c3aed' : '#475569'};
  transition: all 0.15s;
  &:hover { background: #f5f3ff; color: #7c3aed; }

  .icon { font-size: 14px; }
  .label { font-size: 10px; font-weight: 600; }
`

const HelpBoxOnPreview = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: linear-gradient(135deg, rgba(124,58,237,0.06), rgba(37,99,235,0.06));
  border: 1px solid rgba(124,58,237,0.15);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 11.5px;
  color: #475569;
  line-height: 1.45;

  .icon { color: #7c3aed; flex-shrink: 0; margin-top: 1px; }
  strong { color: #7c3aed; font-weight: 700; }
`

const BottomFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 28px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  position: sticky;
  bottom: 0;
  z-index: 30;

  @media (max-width: 1100px) {
    padding: 12px 16px;
  }
`

const FooterNote = styled.div`
  font-size: 12.5px;
  color: #94a3b8;
`

/* === 객체 선택 모달 (우측 'AR 요소 설정 → 3D 모델 선택' 클릭 시) === */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 15, 30, 0.55);
  display: ${p => p.$open ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 9999;
`

const ModalCard = styled.div`
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 30px 80px rgba(0,0,0,0.30);
  width: 100%;
  max-width: 720px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;

  h3 { margin: 0; font-size: 16px; font-weight: 700; color: #0f172a; }
  button {
    width: 32px; height: 32px;
    border-radius: 50%;
    border: none;
    background: #f8fafc;
    color: #475569;
    cursor: pointer;
    font-size: 16px;
    &:hover { background: #e2e8f0; color: #0f172a; }
  }
`

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
`

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 10px;
  margin-top: 12px;
`

const ModalFooter = styled.div`
  padding: 14px 20px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .info { font-size: 12px; color: #64748b; }
`

export default function CreateDetailPage() {
  const { theme } = useTheme()
  const { isLoggedIn, user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  // 임시 저장 키
  const TEMP_SAVE_KEY = `quiz_detail_temp_save_${user?.id || 'guest'}`
  
  // 임시 저장된 데이터 복원 함수
  const getInitialData = () => {
    // 새로고침으로 인해 location.state가 없는 경우 localStorage에서 복원
    if (!location.state) {
      try {
        const savedData = localStorage.getItem(TEMP_SAVE_KEY)
        if (savedData) {
          const parsedData = JSON.parse(savedData)
          console.log('🔄 CreateDetailPage 임시 저장된 데이터 복원:', parsedData)
          return parsedData
        }
      } catch (error) {
        console.error('CreateDetailPage 임시 저장 데이터 복원 실패:', error)
      }
    }
    return location.state || {}
  }
  
  const initialData = getInitialData()

  // 편집 가능한 필드는 useState로 (1차 디자인 적용 시 input/dropzone 사용)
  const [title, setTitle] = useState(initialData.title || '')
  const [description, setDescription] = useState(initialData.description || '')
  const [coverImage, setCoverImage] = useState(initialData.coverImage || null)
  const [previewImages, setPreviewImages] = useState(initialData.previewImages || [])
  const [viewMode, setViewMode] = useState('3d') // '3d' | 'mobile' (AR 미리보기 토글)
  const [showAllChips, setShowAllChips] = useState(false)
  const [showObjectModal, setShowObjectModal] = useState(false)
  const coverInputRef = React.useRef(null)
  const previewInputRef = React.useRef(null)

  // 변경 불가 props (CreatePage에서 전달받은 것)
  const {
    isPrivate = initialData.isPrivate || false,
    questionId = initialData.questionId || null, // 퀴즈 ID
    selectedObjects = initialData.selectedObjects || [], // 이미 선택된 객체들
    currentObjectIndex = initialData.currentObjectIndex || 0, // 현재 작업 중인 객체 인덱스
    isAddingMore = initialData.isAddingMore || false, // 추가 모드인지 여부
    questions = initialData.questions || null, // CSV/Excel에서 로드된 문제 데이터
    isFromExcel = initialData.isFromExcel || false, // Excel에서 왔는지 여부
    excelDataByObject = initialData.excelDataByObject || null // 오브젝트별 Excel 데이터
  } = initialData
  
  // Excel 데이터를 오브젝트별로 그룹화하고 문제 객체로 변환
  const [excelDataByObjectState, setExcelDataByObjectState] = useState(() => {
    if (excelDataByObject) {
      return excelDataByObject
    }

    // questions가 있고 isFromExcel이 true이면 오브젝트별로 그룹화하고 변환
    if (questions && isFromExcel && Array.isArray(questions)) {
      const grouped = {}
      questions.forEach((item, index) => {
        const objectNum = String(item['오브젝트번호'] || item.오브젝트번호 || '1')
        if (!grouped[objectNum]) {
          grouped[objectNum] = []
        }

        // 원본 CSV 데이터를 문제 객체로 변환
        const correctAnswerIndex = parseInt(item['정답번호'] || item.정답번호 || '1') - 1
        const choiceTypes = [false, false, false, false]
        if (correctAnswerIndex >= 0 && correctAnswerIndex < 4) {
          choiceTypes[correctAnswerIndex] = true
        }

        // 문제번호를 숫자로 파싱
        const questionNum = item['문제번호'] || item.문제번호
        const parsedQuestionNum = questionNum ? parseInt(questionNum) : (index + 1)

        grouped[objectNum].push({
          id: `excel_question_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 8)}`,
          timeLimit: 20,
          question: item['문제내용'] || item.문제내용 || '',
          choices: [
            item['선택지1'] || item.선택지1 || '',
            item['선택지2'] || item.선택지2 || '',
            item['선택지3'] || item.선택지3 || '',
            item['선택지4'] || item.선택지4 || ''
          ],
          choiceTypes: choiceTypes,
          attachments: [],
          isAttachmentOpen: false,
          assignedObjectId: objectNum,
          originalQuestionNum: parsedQuestionNum
        })
      })
      console.log('📊 CreateDetailPage에서 Excel 데이터 그룹화 및 변환:', grouped)
      return grouped
    }

    return null
  })

  // 선택된 오브젝트가 몇 번째 오브젝트인지 추적 (Excel 오브젝트 번호와 매칭)
  const [objectIndexMapping, setObjectIndexMapping] = useState({})

  // 상태 관리
  const [activeTab, setActiveTab] = useState(initialData.activeTab || '')
  const [selectedCategory, setSelectedCategory] = useState(initialData.selectedCategory || null)
  const [selectedObject, setSelectedObject] = useState(initialData.selectedObject || null)
  const [quizObjects, setQuizObjects] = useState(selectedObjects) // 퀴즈에 포함된 객체들
  const [categories, setCategories] = useState([])
  const [subcategories, setSubcategories] = useState([])
  const [objects, setObjects] = useState([])
  const [showAddObjectModal, setShowAddObjectModal] = useState(false)
  const [unityPrefabs, setUnityPrefabs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showDropdown, setShowDropdown] = useState(false)

  // 자동 저장 기능
  useEffect(() => {
    if (user?.id) {
      const saveToLocalStorage = () => {
        try {
          const dataToSave = {
            title,
            description,
            isPrivate,
            coverImage,
            previewImages,
            selectedObjects: quizObjects,
            isAddingMore,
            activeTab,
            selectedCategory,
            selectedObject,
            timestamp: new Date().toISOString()
          }
          localStorage.setItem(TEMP_SAVE_KEY, JSON.stringify(dataToSave))
          console.log('💾 CreateDetailPage 자동 저장 완료:', new Date().toLocaleTimeString())
        } catch (error) {
          console.error('CreateDetailPage 자동 저장 실패:', error)
        }
      }

      const timeoutId = setTimeout(saveToLocalStorage, 1000) // 1초 후 저장
      return () => clearTimeout(timeoutId)
    }
  }, [title, description, isPrivate, coverImage, previewImages, quizObjects, isAddingMore, activeTab, selectedCategory, selectedObject, user?.id, TEMP_SAVE_KEY])

  // Load dynamic Unity prefabs list (non-breaking): place `unity-prefabs.json` under `public/`
  useEffect(() => {
    const loadUnityPrefabs = async () => {
      try {
        const res = await fetch('/unity-prefabs.json', { cache: 'no-store' })
        if (res.ok) {
          const data = await res.json()
          if (Array.isArray(data)) setUnityPrefabs(data)
        }
      } catch (e) {
        // ignore; keep empty list if not present
      }
    }
    loadUnityPrefabs()
  }, [])

  // Dynamic Unity objects sourced from unity-prefabs.json (non-breaking)
  const unityObjects = (unityPrefabs || []).map((item, idx) => ({
    object_id: `unity:${item.name}`,
    name: item.name,
    image_url: item.thumbnail || item.file || `/obj/${idx + 1}.png`,
    prefabKey: item.prefabKey || null,
  }))
  const displayObjects = (objects && objects.length > 0) ? objects : unityObjects

  // Resolve current categoryId from loaded categories
  const currentCategoryId = React.useMemo(() => {
    const match = (categories || []).find(c => c?.name === activeTab)
    return match?.category_id || null
  }, [categories, activeTab])

  // 새로고침 방지 경고
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (quizObjects.length > 0) {
        e.preventDefault()
        e.returnValue = '선택된 객체가 있습니다. 정말로 페이지를 떠나시겠습니까?'
        return e.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [quizObjects])
  
  // 선택된 객체들 표시 텍스트 생성
  const getSelectedObjectsText = () => {
    if (quizObjects.length === 0) return ''
    if (quizObjects.length === 1) return quizObjects[0].name
    return `${quizObjects[0].name} 외 ${quizObjects.length - 1}개`
  }
  
  // 탭 목록 (DB에서 동적으로 로드)
  const [tabs, setTabs] = useState([])


  // 카테고리 캐시 (중복 요청 방지)
  const categoriesCacheRef = React.useRef(null);

  // 카테고리 데이터 로드
  const loadCategories = async () => {
    try {
      // 캐시 확인
      if (categoriesCacheRef.current) {
        setCategories(categoriesCacheRef.current);

        // 카테고리에서 고유한 name들을 추출하여 탭 생성
        const uniqueNames = [...new Set(categoriesCacheRef.current.map(cat => cat.name))];
        setTabs(uniqueNames);

        return categoriesCacheRef.current;
      }

      const response = await fetch('/categories/categories')
      const data = await response.json() // Direct JSON parsing (faster than text() + JSON.parse())

      if (data.success) {
        categoriesCacheRef.current = data.categories;
        setCategories(data.categories)

        // 카테고리에서 고유한 name들을 추출하여 탭 생성
        const uniqueNames = [...new Set(data.categories.map(cat => cat.name))];
        setTabs(uniqueNames);

        // activeTab이 아직 설정되지 않았거나 유효하지 않으면 첫 번째 탭으로 설정
        if (uniqueNames.length > 0 && !uniqueNames.includes(activeTab)) {
          setActiveTab(uniqueNames[0]);
        }

        return data.categories;
      } else {
        console.error('카테고리 로드 실패:', data.error)
        return [];
      }
    } catch (error) {
      console.error('카테고리 로드 실패:', error)
      return [];
    }
  }

  // 서브카테고리 로드 (카테고리별 description 목록)
  // 서브카테고리 캐시 (중복 요청 방지)
  const subcategoriesCacheRef = React.useRef({});

  const loadSubcategories = async (categoryName) => {
    try {
      // 캐시 확인
      if (subcategoriesCacheRef.current[categoryName]) {
        const categorySubcategories = subcategoriesCacheRef.current[categoryName];
        setSubcategories(categorySubcategories);

        // 첫 번째 서브카테고리를 기본 선택
        if (categorySubcategories.length > 0) {
          const firstSubcategory = categorySubcategories[0];
          setSelectedCategory(firstSubcategory);
          loadObjects(categoryName, firstSubcategory.description);
        }
        return;
      }

      const response = await fetch(`/categories/categories`)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data.success) {
        // 같은 name을 가진 카테고리들만 필터링
        const categorySubcategories = data.categories.filter(cat => cat.name === categoryName);

        // 캐시 저장
        subcategoriesCacheRef.current[categoryName] = categorySubcategories;

        setSubcategories(categorySubcategories);

        // 첫 번째 서브카테고리를 기본 선택
        if (categorySubcategories.length > 0) {
          const firstSubcategory = categorySubcategories[0];
          setSelectedCategory(firstSubcategory);
          loadObjects(categoryName, firstSubcategory.description);
        } else {
          setSelectedCategory(null);
          setObjects([]);
        }
      } else {
        console.error('서브카테고리 로드 API 실패:', data.error);
        setSubcategories([]);
        setSelectedCategory(null);
        setObjects([]);
      }
    } catch (error) {
      console.error('서브카테고리 로드 실패:', error)
      setSubcategories([])
      setSelectedCategory(null)
      setObjects([])
    }
  }

  // AR 객체 로드
  const loadObjects = async (categoryName, subcategoryDescription) => {
    try {
      if (!subcategoryDescription) {
        console.log('서브카테고리 설명이 없어서 객체 로드 건너뜀')
        setObjects([])
        return
      }

      const url = `/categories/categories/name/${encodeURIComponent(categoryName)}/subcategory/${encodeURIComponent(subcategoryDescription)}/objects`
      console.log('객체 로드 URL:', url)
      
      const response = await fetch(url)
      const data = await response.json()
      
      console.log('객체 로드 응답:', data)
      
      if (data.success) {
        // object_id를 id로 매핑하여 CreateQuizPage와 호환되도록 함
        const mappedObjects = data.objects.map(obj => ({
          ...obj,
          id: obj.object_id // object_id를 id로 매핑
        }))
        console.log(`${mappedObjects.length}개 객체 로드됨:`, mappedObjects)
        setObjects(mappedObjects)
      } else {
        console.log('객체 로드 실패:', data.error)
        setObjects([])
      }
    } catch (error) {
      console.error('객체 로드 실패:', error)
      setObjects([])
    }
  }

  useEffect(() => {
    // 로그인 체크
    if (!isLoggedIn) {
      alert('로그인이 필요한 페이지입니다.')
      navigate('/login')
      return
    }

    // 제목이 없으면 CreatePage로 리다이렉트
    if (!title) {
      navigate('/create')
      return
    }

    // 병렬 로딩: 카테고리와 서브카테고리를 동시에 로드
    const initializeData = async () => {
      const startTime = performance.now();

      // 병렬 실행
      await Promise.all([
        loadCategories(),
        activeTab ? loadSubcategories(activeTab) : Promise.resolve()
      ]);

      const endTime = performance.now();
      console.log(`⚡ 데이터 로드 완료: ${(endTime - startTime).toFixed(0)}ms`);
    };

    initializeData();
  }, [isLoggedIn, title, navigate])

  // 탭 변경 시 서브카테고리 로드
  useEffect(() => {
    if (activeTab) {
      loadSubcategories(activeTab)
    }
  }, [activeTab])
  
  // 객체 선택 핸들러
  const handleObjectSelect = (object) => {
    setSelectedObject(object)
  }

  // 카테고리 선택 핸들러
  const handleCategorySelect = (subcategory) => {
    setSelectedCategory(subcategory)
    loadObjects(activeTab, subcategory.description)
    setSelectedObject(null) // 객체 선택 초기화
    setShowDropdown(false) // 드롭다운 닫기
  }
  
  
  // 다음 단계로 이동 (CreateQuiz 페이지로)
  const handleProceedToQuiz = () => {
    if (!selectedObject) {
      alert('AR 객체를 선택해주세요.')
      return
    }
    
    // 새로운 객체를 퀴즈 목록에 추가
    const updatedQuizObjects = [...quizObjects, selectedObject]
    
    console.log('다음 단계로 이동:', {
      title,
      description,
      isPrivate,
      selectedObject,
      quizObjects: updatedQuizObjects,
      activeTab,
      selectedCategory,
      questions: questions ? `${questions.length}개 문제` : 'none'
    })

    // 임시 저장 데이터 삭제 (다음 단계로 정상 진행)
    try {
      localStorage.removeItem(TEMP_SAVE_KEY);
      console.log('🗑️ CreateDetailPage 임시 저장 데이터 삭제 완료');
    } catch (error) {
      console.error('CreateDetailPage 임시 저장 데이터 삭제 실패:', error);
    }

    // Excel 데이터가 있는 경우, 현재 선택한 오브젝트에 해당하는 데이터 결정
    let questionsToPass = questions
    let currentExcelObjectId = null
    let newObjectIndexMapping = { ...objectIndexMapping }

    if (excelDataByObjectState && selectedObject) {
      // 현재 선택한 오브젝트가 몇 번째인지 확인
      const selectedObjectDbId = String(selectedObject.object_id || selectedObject.id || '')

      // 이미 매핑이 있으면 사용, 없으면 새로 생성
      if (!newObjectIndexMapping[selectedObjectDbId]) {
        // 현재까지 선택된 오브젝트 수 + 1이 Excel 오브젝트 번호
        const excelObjectNum = String(updatedQuizObjects.length)
        newObjectIndexMapping[selectedObjectDbId] = excelObjectNum
        setObjectIndexMapping(newObjectIndexMapping)
        console.log(`📊 새 오브젝트 매핑: DB ID ${selectedObjectDbId} → Excel 오브젝트 ${excelObjectNum}`)
      }

      const excelObjectNum = newObjectIndexMapping[selectedObjectDbId]

      // excelDataByObjectState에서 해당 Excel 오브젝트 번호의 데이터 찾기
      if (excelDataByObjectState[excelObjectNum]) {
        console.log(`📊 오브젝트 ${excelObjectNum}의 Excel 데이터 발견 (${excelDataByObjectState[excelObjectNum].length}개 문제)`)
        currentExcelObjectId = excelObjectNum
        questionsToPass = excelDataByObjectState[excelObjectNum]
      } else {
        console.log(`⚠️ Excel 오브젝트 ${excelObjectNum}의 데이터가 없습니다`)
        console.log(`  - DB ID: ${selectedObjectDbId}`)
        console.log(`  - 사용 가능한 Excel 오브젝트:`, Object.keys(excelDataByObjectState))
      }
    }

    // CreateQuiz 페이지로 라우팅
    navigate('/createquiz', {
      state: {
        title,
        description,
        isPrivate,
        questionId,
        selectedObject,
        selectedObjects: updatedQuizObjects,
        currentObjectIndex: updatedQuizObjects.length - 1, // 새로 추가된 객체의 인덱스
        activeTab,
        selectedCategory,
        questions: questionsToPass, // CSV/Excel 문제 데이터 전달
        isFromExcel, // Excel에서 왔는지 플래그
        coverImage, // 커버 이미지
        excelDataByObject: excelDataByObjectState, // 오브젝트별 Excel 데이터 전달
        currentExcelObjectId // 현재 처리할 오브젝트 ID
      }
    })
  }
  
  // 퀴즈 세트 완료 처리
  const handleCompleteQuizSet = async () => {
    if (quizObjects.length === 0) {
      alert('최소 하나의 AR 객체를 선택해주세요.')
      return
    }
    
    // 현재 선택된 물체 정보는 저장하지 않음 (버림)
    console.log('현재 선택된 물체는 저장하지 않습니다:', selectedObject)
    
    try {
      console.log('퀴즈 세트 완료:', {
        title,
        description,
        isPrivate,
        quizObjects,
        note: '현재 선택된 물체는 제외됨'
      })
      
      // 퀴즈가 이미 저장되어 있으므로 set_is_saved만 업데이트
      
      // set_is_saved를 true로 업데이트
      if (questionId) {
        try {
          const updateResponse = await fetch('/auth/update-quiz-saved-status', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              questionId: questionId,
              isSaved: true
            }),
          });
          
          if (updateResponse.ok) {
            console.log('✅ 퀴즈 저장 상태 업데이트 완료');
          } else {
            console.warn('⚠️ 퀴즈 저장 상태 업데이트 실패');
          }
        } catch (error) {
          console.error('💥 퀴즈 저장 상태 업데이트 오류:', error);
        }
      }
      
      // 성공 메시지 표시 (현재 선택된 객체는 제외하고 카운트)
      alert(`🎉 "${title}" 퀴즈 세트가 완료되었습니다!\n\n포함된 객체: ${getSelectedObjectsText()}\n총 ${quizObjects.length}개의 AR 객체가 포함되었습니다.`)
      
      // 홈 페이지로 이동
      navigate('/', { 
        state: { 
          newQuizCreated: true,
          newQuizId: questionId,
          newQuizTitle: title 
        } 
      })
      
    } catch (error) {
      console.error('퀴즈 세트 저장 실패:', error)
      alert('퀴즈 세트 저장 중 오류가 발생했습니다.')
    }
  }

  // 이전 버튼 핸들러
  const handleBack = () => {
    // 물체가 하나라도 있는 경우, 현재 물체의 퀴즈 생성 화면으로 이동
    if (quizObjects.length > 0 && currentObjectIndex >= 0) {
      // 현재 물체로 돌아감 (currentObjectIndex 유지)
      const targetIndex = currentObjectIndex;

      // currentObjectIndex보다 뒤에 있는 물체 데이터를 삭제 (currentObjectIndex+1부터 삭제)
      const trimmedQuizObjects = quizObjects.slice(0, currentObjectIndex + 1);
      const targetObject = trimmedQuizObjects[targetIndex];

      console.log('🔙 CreateDetailPage 이전 버튼: 물체', targetIndex + 1, '번으로 이동, 이후 데이터 삭제');
      console.log('   삭제 전 물체 개수:', quizObjects.length, '→ 삭제 후:', trimmedQuizObjects.length);

      navigate('/createquiz', {
        state: {
          title,
          description,
          isPrivate,
          selectedObject: targetObject,
          questionId,
          selectedObjects: trimmedQuizObjects, // 잘라낸 배열 전달
          currentObjectIndex: targetIndex, // 현재 물체의 인덱스 전달
          isAddingMore: true,
          activeTab,
          selectedCategory,
          coverImage,
          excelDataByObject: excelDataByObjectState
        }
      });
    } else {
      // 물체가 없으면 Create 페이지로 이동
      console.log('🔙 CreateDetailPage 이전 버튼: Create 페이지로 이동');
      navigate('/create', {
        state: { title, description, isPrivate }
      });
    }
  }

  return (
    <>
      <TopBar>
        <BackBtn onClick={() => navigate('/create')}>← 만들기로 돌아가기</BackBtn>
        <StepperWrap>
          {[
            { n: 1, label: '기본 정보' },
            { n: 2, label: '오브젝트 등록', active: true },
            { n: 3, label: '문제 만들기' },
            { n: 4, label: '배포하기' },
          ].map((s, i, arr) => (
            <React.Fragment key={s.n}>
              <StepDot $active={s.active}>
                <span className="num">{s.n}</span>
                <span className="label">{s.label}</span>
              </StepDot>
              {i < arr.length - 1 && <StepLine />}
            </React.Fragment>
          ))}
        </StepperWrap>
        <TopActions>
          <SaveDraftBtn onClick={handleBack}>← 이전 단계</SaveDraftBtn>
          <NextStepBtn onClick={handleProceedToQuiz} disabled={!selectedObject}>다음 단계 →</NextStepBtn>
        </TopActions>
      </TopBar>
    <Container>
      {/* 데스크톱 레이아웃 (681px 이상) — 새 디자인 (옵션 B 본문) */}
      <Stage>
        <PanelCol>
          {/* L1·L2: 퀴즈 제목 (1단계에서 입력 — 표시만) + ID 한 줄 */}
          <PanelCard>
            <PanelLabel>퀴즈 제목</PanelLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <TitleStrong style={{ fontSize: 16 }}>{title || '(제목 없음)'}</TitleStrong>
              {questionId && (
                <span style={{
                  fontSize: 11,
                  color: '#475569',
                  background: '#f1f5f9',
                  padding: '3px 10px',
                  borderRadius: 999,
                  fontWeight: 600,
                }}>ID: {questionId}</span>
              )}
            </div>
          </PanelCard>

          {/* L3·L4: AR 주제(카테고리) — dropdown */}
          <PanelCard>
            <SectionTitle>오브젝트 주제 선택</SectionTitle>
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: '#ffffff',
                fontSize: 13.5,
                color: '#0f172a',
                cursor: 'pointer',
                appearance: 'auto',
              }}
            >
              <option value="" disabled>주제를 선택하세요</option>
              {tabs.map(tab => (
                <option key={tab} value={tab}>{tab}</option>
              ))}
            </select>
          </PanelCard>

          {/* L5·L6·L7: 세부 분류 dropdown */}
          <PanelCard>
            <SectionTitle>세부 분류</SectionTitle>
            <DropdownContainerNew>
              <SubDropdown onClick={() => setShowDropdown(!showDropdown)}>
                <span>{selectedCategory ? selectedCategory.description : '카테고리를 선택하세요'}</span>
                <ChevronRight
                  size={16}
                  style={{
                    transform: showDropdown ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </SubDropdown>
              {showDropdown && (
                <SubMenu>
                  {subcategories.map((subcategory) => (
                    <SubMenuItem
                      key={subcategory.category_id}
                      className={selectedCategory?.category_id === subcategory.category_id ? 'selected' : ''}
                      onClick={() => handleCategorySelect(subcategory)}
                    >
                      {subcategory.description}
                      <div className="sub">{subcategory.name} 분야</div>
                    </SubMenuItem>
                  ))}
                </SubMenu>
              )}
            </DropdownContainerNew>
          </PanelCard>

          {/* 오브젝트 후보 그리드 (현재 카테고리/세부 분류 결과) */}
          <PanelCard>
            <SectionTitle>오브젝트 선택 ({quizObjects.length} / 10)</SectionTitle>
            {displayObjects.length > 0 ? (
              <PreviewImagesGrid>
                {displayObjects.map((object) => {
                  const isInQuiz = quizObjects.some(o => o.object_id === object.object_id)
                  return (
                    <PreviewSlot
                      key={object.object_id}
                      title={object.name}
                      onClick={() => {
                        handleObjectSelect(object)
                        setQuizObjects(prev => {
                          if (prev.find(o => o.object_id === object.object_id)) {
                            return prev.filter(o => o.object_id !== object.object_id)
                          }
                          if (prev.length >= 10) {
                            alert('최대 10개까지 선택할 수 있습니다.')
                            return prev
                          }
                          return [...prev, object]
                        })
                      }}
                      style={{
                        cursor: 'pointer',
                        border: isInQuiz ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                        background: isInQuiz ? '#f5f3ff' : '#ffffff',
                        padding: 6,
                        transition: 'all 0.15s',
                      }}
                    >
                      <img
                        src={object.image_url}
                        alt={object.name}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                      />
                      {isInQuiz && (
                        <span style={{
                          position: 'absolute',
                          top: 4, right: 4,
                          width: 18, height: 18,
                          borderRadius: '50%',
                          background: '#7c3aed',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                        }}>✓</span>
                      )}
                    </PreviewSlot>
                  )
                })}
              </PreviewImagesGrid>
            ) : selectedCategory ? (
              <EmptyNote>{selectedCategory.name}에 등록된 AR 객체가 없습니다.</EmptyNote>
            ) : (
              <EmptyNote>위에서 카테고리와 세부 분류를 먼저 선택하세요.</EmptyNote>
            )}
          </PanelCard>
        </PanelCol>

        <PanelCol>
          {/* R1: 선택된 객체 배너 + 퀴즈 완료 버튼(R6) */}
          {isAddingMore && quizObjects.length > 0 && (
            <SelectedBanner>
              현재 퀴즈 세트: {getSelectedObjectsText()}
              <NextStepBtn
                onClick={handleCompleteQuizSet}
                style={{ marginLeft: '12px', padding: '6px 14px', fontSize: '12px', verticalAlign: 'middle' }}
              >
                퀴즈 완료
              </NextStepBtn>
            </SelectedBanner>
          )}

          {/* 오브젝트 미리보기 (도움말 박스만 제거) */}
          <PanelCard>
            <SectionTitle>오브젝트 미리보기</SectionTitle>

            <PreviewWrap>
              {selectedObject ? (
                <PreviewObjectImg src={selectedObject.image_url} alt={selectedObject.name} />
              ) : (
                <div style={{ position: 'relative', width: '60%', maxWidth: 320, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <img src="/ArStage.png" alt="AR Stage" style={{ width: '100%', display: 'block', filter: 'drop-shadow(0 12px 18px rgba(217,119,6,0.25))' }} />
                </div>
              )}
              <HelpFloat onClick={() => alert('좌측 「오브젝트 선택」에서 2D 오브젝트를 골라주세요.')}>
                💡 도움말
              </HelpFloat>
            </PreviewWrap>

            {selectedObject && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{selectedObject.name}</div>
                {selectedObject.description && (
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{selectedObject.description}</div>
                )}
              </div>
            )}
          </PanelCard>

          {/* 선택된 오브젝트 영구 표시 (카테고리 바꿔도 유지) */}
          <PanelCard>
            <SectionTitle>선택된 오브젝트 ({quizObjects.length} / 10)</SectionTitle>
            {quizObjects.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {quizObjects.map((obj, idx) => (
                  <div
                    key={obj.object_id || idx}
                    title={obj.name}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '5px 10px 5px 5px',
                      background: '#f5f3ff',
                      border: '1px solid #c4b5fd',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#5b21b6',
                    }}
                  >
                    <img
                      src={obj.image_url}
                      alt={obj.name}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: '#ffffff',
                        objectFit: 'contain',
                        padding: 2,
                      }}
                    />
                    <span style={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {obj.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuizObjects(prev => prev.filter((_, i) => i !== idx))}
                      aria-label="제거"
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        border: 'none',
                        background: 'rgba(91, 33, 182, 0.12)',
                        color: '#5b21b6',
                        cursor: 'pointer',
                        fontSize: 12,
                        lineHeight: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >×</button>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyNote>왼쪽에서 오브젝트를 선택하면 여기에 표시됩니다.</EmptyNote>
            )}
          </PanelCard>

          {/* AR 요소 설정 패널 제거 (2D 전용 — 애니메이션/사운드 미사용, 오브젝트 선택은 미리보기 CTA로 이동) */}
        </PanelCol>
      </Stage>

      {/* 하단 footer 제거 — TopBar에 이미 이전/임시저장/다음 단계 있음 */}

      {/* 3D 모델 선택 모달 (좌측 패널의 객체 그리드 대체) */}
      <ModalOverlay $open={showObjectModal} onClick={() => setShowObjectModal(false)}>
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <h3>3D 모델 선택</h3>
            <button type="button" onClick={() => setShowObjectModal(false)} aria-label="닫기">×</button>
          </ModalHeader>
          <ModalBody>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#64748b' }}>
              {activeTab && <span style={{ fontWeight: 600, color: '#7c3aed' }}>{activeTab}</span>}
              {selectedCategory?.description && (
                <>
                  <span>›</span>
                  <span>{selectedCategory.description}</span>
                </>
              )}
            </div>

            {displayObjects.length > 0 ? (
              <ModalGrid>
                {displayObjects.map((object) => (
                  <ObjectCardNew
                    key={object.object_id}
                    $selected={selectedObject?.object_id === object.object_id}
                    onClick={() => {
                      handleObjectSelect(object)
                      setShowObjectModal(false)
                    }}
                    title={object.name}
                  >
                    <img src={object.image_url} alt={object.name} />
                  </ObjectCardNew>
                ))}
              </ModalGrid>
            ) : selectedCategory ? (
              <EmptyNote>{selectedCategory.name}에 등록된 AR 객체가 없습니다.</EmptyNote>
            ) : (
              <EmptyNote>좌측에서 카테고리와 세부 분류를 먼저 선택하세요.</EmptyNote>
            )}
          </ModalBody>
          <ModalFooter>
            <span className="info">
              {selectedObject ? `선택: ${selectedObject.name}` : '아직 선택된 모델이 없습니다.'}
            </span>
            <SaveDraftBtn onClick={() => setShowObjectModal(false)}>닫기</SaveDraftBtn>
          </ModalFooter>
        </ModalCard>
      </ModalOverlay>

      {/* 모바일 레이아웃 (680px 이하) */}
      <MobileContainer>
        {/* 퀴즈 제목 표시 */}
        {title && (
          <QuizTitleHeader>
            <QuizTitleText>퀴즈 제목</QuizTitleText>
            <QuizTitle>{title}</QuizTitle>
            {questionId && <QuizId>ID: {questionId}</QuizId>}
          </QuizTitleHeader>
        )}
        
        {/* 선택된 객체들 표시 */}
        {isAddingMore && quizObjects.length > 0 && (
          <div style={{ 
            padding: '8px 15px', 
            backgroundColor: '#e3f2fd', 
            borderRadius: '8px', 
            margin: '10px 15px', 
            fontSize: '12px', 
            fontWeight: '600',
            color: '#1976d2',
            textAlign: 'center'
          }}>
            현재 퀴즈 세트: {getSelectedObjectsText()}
          </div>
        )}
        {/* AR 스테이지 */}
        <ARStageContainer>
          <ARStage>
            {selectedObject && (
              <ARObject>
                <img src={selectedObject.image_url} alt={selectedObject.name} />
              </ARObject>
            )}
          </ARStage>
          
          <NavigationContainer>
            <NavButton onClick={handleBack}>
              <ChevronLeft size={20} />
              이전
            </NavButton>
            <NavButton onClick={handleProceedToQuiz} disabled={!selectedObject}>
              {isAddingMore && quizObjects.length > 0 ? '추가' : '다음'}
              <ChevronRight size={20} />
            </NavButton>
            {isAddingMore && quizObjects.length > 0 && (
              <NavButton onClick={handleCompleteQuizSet} style={{ fontSize: '14px', padding: '8px 16px' }}>
                퀴즈 완료
              </NavButton>
            )}
          </NavigationContainer>
        </ARStageContainer>
        
        {/* 탭 네비게이션 */}
        <TabsContainer>
          <TabsWrapper>
            {tabs.map(tab => (
              <Tab
                key={tab}
                $active={activeTab === tab}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </Tab>
            ))}
          </TabsWrapper>
        </TabsContainer>
        
        {/* 카테고리 선택 */}
        <CategorySelector>
          <DropdownContainer>
            <DropdownButton onClick={() => setShowDropdown(!showDropdown)}>
              <span>{selectedCategory ? selectedCategory.description : '카테고리를 선택하세요'}</span>
              <ChevronRight size={16} style={{ 
                transform: showDropdown ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} />
            </DropdownButton>
            
            {showDropdown && (
              <DropdownMenu>
                {subcategories.map((subcategory) => (
                  <DropdownItem
                    key={subcategory.category_id}
                    className={selectedCategory?.category_id === subcategory.category_id ? 'selected' : ''}
                    onClick={() => handleCategorySelect(subcategory)}
                  >
                    {subcategory.description}
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                      {subcategory.name} 분야
                    </div>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            )}
          </DropdownContainer>
        </CategorySelector>
        
        {/* 객체 그리드 */}
        <ObjectGrid>
          <ObjectGridContainer>
            {objects.map((object) => (
              <ObjectCard
                key={object.object_id}
                $selected={selectedObject?.object_id === object.object_id}
                onClick={() => handleObjectSelect(object)}
              >
                <img src={object.image_url} alt={object.name} />
              </ObjectCard>
            ))}
            {displayObjects.length === 0 && selectedCategory && (
              <div style={{ 
                gridColumn: '1 / -1', 
                textAlign: 'center', 
                color: '#666', 
                padding: '40px 20px' 
              }}>
                {selectedCategory.name}에 등록된 AR 객체가 없습니다.
              </div>
            )}
          </ObjectGridContainer>
        </ObjectGrid>

        
      </MobileContainer>

      {/* Global floating button and modal so it shows on all breakpoints */}
      {/* <FixedAddButton onClick={() => setShowAddObjectModal(true)}>
        <Plus size={18} /> 오브젝트 추가
      </FixedAddButton> */}
      <AddObjectModal
        open={showAddObjectModal}
        onClose={() => setShowAddObjectModal(false)}
        subcategories={subcategories}
        defaultSubcategoryId={selectedCategory?.subcategory_id}
        categoryId={currentCategoryId}
        categoryName={activeTab}
        subcategoryDescription={selectedCategory?.description}
        onCreated={() => {
          try {
            if (activeTab && selectedCategory?.description) {
              loadObjects(activeTab, selectedCategory.description)
            }
          } finally {
            setShowAddObjectModal(false)
          }
        }}
      />

    </Container>
    </>
  )
}
